import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

interface SectionRejectControlProps {
  sectionLabel: string;
  sectionPath: string;
  staged: { pendingId: string; commentText: string } | null;
  /** True when the report is in a state where the CO can act (SIGNED). */
  active: boolean;
  onStage: (commentText: string) => void;
  onEdit: (pendingId: string, commentText: string) => void;
  onClear: (pendingId: string) => void;
  /** When provided, the section label becomes a button that scrolls the main view. */
  onJumpToSection?: () => void;
}

/**
 * Per-section reject control (spec §1). The CO clicks **Reject** to stage a section-level
 * rejection with a required comment. Staged rejections enable the global "Reject Report"
 * button and disable Approve. Only one rejection per section per cycle.
 */
export function SectionRejectControl({
  sectionLabel,
  sectionPath,
  staged,
  active,
  onStage,
  onEdit,
  onClear,
  onJumpToSection,
}: SectionRejectControlProps) {
  const theme = useTheme();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(staged?.commentText ?? "");

  const startEdit = () => {
    setDraft(staged?.commentText ?? "");
    setEditing(true);
  };
  const cancel = () => {
    setDraft(staged?.commentText ?? "");
    setEditing(false);
  };
  const save = () => {
    const text = draft.trim();
    if (!text) return;
    if (staged) {
      onEdit(staged.pendingId, text);
    } else {
      onStage(text);
    }
    setEditing(false);
  };

  return (
    <Paper
      elevation={0}
      data-section-path={sectionPath}
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: `1px solid ${
          staged ? theme.palette.error.main : theme.palette.divider
        }`,
        bgcolor: staged
          ? alpha(theme.palette.error.main, 0.05)
          : "background.paper",
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {onJumpToSection ? (
          <Tooltip title="Scroll to this section in the report">
            <Typography
              component="button"
              type="button"
              variant="body2"
              onClick={onJumpToSection}
              sx={{
                fontWeight: 600,
                flexGrow: 1,
                textAlign: "left",
                background: "none",
                border: 0,
                p: 0,
                color: "inherit",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {sectionLabel}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {sectionLabel}
          </Typography>
        )}
        {staged ? (
          <>
            <Tooltip title="Edit reason">
              <span>
                <IconButton size="small" onClick={startEdit} disabled={!active || editing}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Undo rejection">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onClear(staged.pendingId)}
                  disabled={!active}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<ReportProblemOutlinedIcon />}
            onClick={() => {
              setDraft("");
              setEditing(true);
            }}
            disabled={!active || editing}
          >
            Reject
          </Button>
        )}
      </Box>

      {editing && (
        <Stack spacing={1} mt={1}>
          <TextField
            autoFocus
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            placeholder="Reason for rejecting this section (required)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={cancel}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={!draft.trim()}
              onClick={save}
            >
              {staged ? "Save" : "Stage rejection"}
            </Button>
          </Stack>
        </Stack>
      )}

      {staged && !editing && (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: "background.paper",
            border: `1px dashed ${theme.palette.error.main}`,
            whiteSpace: "pre-wrap",
            color: theme.palette.error.dark,
          }}
        >
          {staged.commentText}
        </Typography>
      )}
    </Paper>
  );
}
